export interface RetrievalDetailOptions {
  /**
   * The turn's prompt carried the saved bid record (title, client, deadline,
   * memory). True for every bid-scoped (Tender Chat) turn.
   */
  hadSavedBidData?: boolean
}

/**
 * Honest retrieval-status line (§2.2, pre-approved copy).
 *
 * Shared by BOTH chat stores running on the orchestrator transport (tender +
 * assistant), so the quiet honest line under a completed answer reads
 * identically on every door — live-streamed AND rehydrated from history.
 * Extracted here (not into either store) so neither store imports the other.
 *
 * Returns the line to show, or `undefined` when retrieval was clean and
 * unbroadened (nothing to disclose).
 */
export function deriveRetrievalDetail(
  status: 'ok' | 'partial' | 'empty' | 'weak' | undefined,
  fallbackUsed: boolean,
  options: RetrievalDetailOptions = {}
): string | undefined {
  if (status === 'empty') {
    // An empty document search only means "general knowledge" when nothing
    // else was in front of the model. With the saved bid record in the prompt
    // the answer may well come from it, so say only what is known.
    return options.hadSavedBidData
      ? 'No matching evidence found.'
      : 'No matching evidence found — answered from general knowledge.'
  }
  if (status === 'partial') return 'Partial match — broadened the search.'
  // `weak` = results came back, but none scored like a real match.
  if (status === 'weak') return 'Weak match — the closest evidence was only loosely related.'
  if (fallbackUsed && (status === 'ok' || status === undefined)) {
    return 'Broadened the search beyond the initial pass.'
  }
  return undefined
}
